import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { getAuthMessages, getRememberedLocale } from "@/lib/authMessages";

/*
 * Forced password change screen. Shown only when the authenticated user's
 * `must_change_password` flag is true (currently the seeded admin).
 */

export default function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const locale = user?.preferred_language || getRememberedLocale();
  const t = getAuthMessages(locale);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr(t.passwordTooShort);
    if (next !== confirm) return setErr(t.passwordMismatch);
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success(t.changeSuccess);
      navigate("/", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8 space-y-3"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-1">
          My English Coach Interview Simulator
        </div>
        <h1
          className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)]"
          data-testid="change-password-title"
        >
          {t.changeTitle}
        </h1>
        <p className="text-sm text-[color:var(--text-secondary)]">
          {t.changeSubtitle} (<span className="font-mono">{user?.email}</span>).
        </p>
        <PwField label={t.currentPassword} value={current} setValue={setCurrent} testId="current" t={t} autoComplete="current-password" />
        <PwField label={`${t.newPassword} (8+)`} value={next} setValue={setNext} testId="next" t={t} autoComplete="new-password" />
        <PwField label={t.confirmNewPassword} value={confirm} setValue={setConfirm} testId="confirm" t={t} autoComplete="new-password" />
        {confirm.length > 0 && next !== confirm && (
          <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]">
            {t.passwordMismatch}
          </div>
        )}
        {err && (
          <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40"
          data-testid="change-password-submit"
        >
          {busy ? t.processing : t.saveNewPassword}
        </button>
        <button
          type="button"
          onClick={logout}
          className="w-full text-xs text-[color:var(--text-secondary)] underline"
          data-testid="change-password-logout"
        >
          {t.logoutWithoutChanging}
        </button>
      </form>
    </div>
  );
}

function PwField({ label, value, setValue, testId, t, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">
        {label}
      </span>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete={autoComplete}
          className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
          data-testid={`change-password-${testId}`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-2 text-[11px] font-medium text-[color:var(--brand)]"
          aria-label={visible ? t.hidePassword : t.showPassword}
        >
          {visible ? t.hidePassword : t.showPassword}
        </button>
      </div>
    </label>
  );
}
